import { apiClient, unwrap } from '@/lib/apiClient';
import type { InstitutionType, InstituteStatus } from '@/constants/institute';
import type { ApiSuccess, PaginationMeta } from '@/types/api';
import type { AddAdminPayload } from '../schemas/admin.schema';
import type {
  Institute,
  InstituteAdmin,
  InstituteDetail,
  InstituteListItem,
  InstituteListParams,
  InstituteSettings,
  PlatformStats,
  ReverseGeocodedAddress,
  SubscriptionIncome,
  SubscriptionPayment,
  SubscriptionReceipts,
  SubscriptionSummary,
} from '@/types/institute';
import type { EmailInstitutePayload } from '../schemas/email.schema';
import type { InstitutePayload, InstituteUpdatePayload } from '../schemas/institute.schema';
import type { SettingsPayload } from '../schemas/settings.schema';
import type { SubscriptionPaymentPayload } from '../schemas/subscriptionPayment.schema';

/** A page of institutes plus the pagination envelope the table needs. */
export interface InstituteListResult {
  items: InstituteListItem[];
  /**
   * How the customers split between schools and colleges, counted over the
   * *same* search, status and state as the page — but ignoring which tab is
   * open, so the inactive tab still says how many are waiting behind it.
   */
  byType: Record<InstitutionType, number>;
  /**
   * How many institutes are in each status, counted over the *same* kind,
   * search and state as the page — but ignoring which status filter is selected.
   */
  byStatus: Record<InstituteStatus | 'ALL', number>;
  meta: PaginationMeta;
}

/** One page of an institute's licence ledger, with totals over all of it. */
export interface SubscriptionPaymentsResult {
  items: SubscriptionPayment[];
  summary: SubscriptionSummary;
  meta: PaginationMeta;
}

/**
 * Every platform-console network call. Components never touch `apiClient`
 * directly (CLAUDE.md §6).
 */
export const institutesApi = {
  list: async (params: InstituteListParams): Promise<InstituteListResult> => {
    // Blank filters are dropped so the URL stays readable and the server does
    // not have to treat "" as "all".
    const query = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== '' && value !== undefined),
    );

    const response = await apiClient.get<
      ApiSuccess<{
        items: InstituteListItem[];
        byType: Record<InstitutionType, number>;
        byStatus?: Record<InstituteStatus | 'ALL', number>;
      }>
    >('/platform/institutes', { params: query });

    const { items, byType, byStatus } = response.data.data;

    return {
      items,
      byType,
      byStatus: byStatus ?? {
        ALL: response.data.meta?.total ?? items.length,
        PENDING_SETUP: 0,
        ACTIVE: 0,
        SUSPENDED: 0,
        ARCHIVED: 0,
      },
      meta: response.data.meta ?? { page: 1, limit: 20, total: items.length, totalPages: 1 },
    };
  },

  get: (uuid: string) =>
    unwrap(apiClient.get<ApiSuccess<InstituteDetail>>(`/platform/institutes/${uuid}`)),

  create: (payload: InstitutePayload) =>
    unwrap(apiClient.post<ApiSuccess<Institute>>('/platform/institutes', payload)),

  update: (uuid: string, payload: InstituteUpdatePayload) =>
    unwrap(apiClient.patch<ApiSuccess<Institute>>(`/platform/institutes/${uuid}`, payload)),

  /**
   * An institute's operating settings. Bank, gateway and SMS credentials live on
   * the same table but are never sent to the browser in either direction.
   */
  getSettings: (uuid: string) =>
    unwrap(apiClient.get<ApiSuccess<InstituteSettings>>(`/platform/institutes/${uuid}/settings`)),

  updateSettings: (uuid: string, payload: SettingsPayload) =>
    unwrap(
      apiClient.patch<ApiSuccess<InstituteSettings>>(`/platform/institutes/${uuid}/settings`, payload),
    ),

  /** The logo is private — this path needs the access token, so use SecureImage. */
  logoPath: (uuid: string) => `/platform/institutes/${uuid}/logo`,

  uploadLogo: (uuid: string, file: File) => {
    const form = new FormData();
    form.append('logo', file);
    // Content-Type is left unset on purpose: the browser must add the
    // multipart boundary itself.
    return unwrap(
      apiClient.post<ApiSuccess<Institute>>(`/platform/institutes/${uuid}/logo`, form, {
        headers: { 'Content-Type': undefined },
      }),
    );
  },

  removeLogo: (uuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/platform/institutes/${uuid}/logo`)),

  /** Stops every login for the institute. Data is untouched. */
  suspend: (uuid: string, reason: string) =>
    unwrap(apiClient.post<ApiSuccess<Institute>>(`/platform/institutes/${uuid}/suspend`, { reason })),

  activate: (uuid: string) =>
    unwrap(apiClient.post<ApiSuccess<Institute>>(`/platform/institutes/${uuid}/activate`, {})),

  /** Takes a suspended institute off the platform. A soft delete — the rows stay. */
  remove: (uuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/platform/institutes/${uuid}`)),

  /**
   * Emails the institute's administrator, with files attached. Always goes to
   * the contact address on the record — the server chooses the recipient.
   */
  emailAdmin: (uuid: string, payload: EmailInstitutePayload, attachments: File[]) => {
    const form = new FormData();
    form.append('subject', payload.subject);
    form.append('message', payload.message);
    for (const file of attachments) form.append('attachments', file);
    // Content-Type left unset, as for the logo: the browser adds the boundary.
    return unwrap(
      apiClient.post<ApiSuccess<null>>(`/platform/institutes/${uuid}/email`, form, {
        headers: { 'Content-Type': undefined },
      }),
    );
  },

  /** Everyone who administers the institute, the primary first. */
  listAdmins: (uuid: string) =>
    unwrap(apiClient.get<ApiSuccess<InstituteAdmin[]>>(`/platform/institutes/${uuid}/admins`)),

  /** Adds an administrator; the server emails them a link to set their password. */
  addAdmin: (uuid: string, payload: AddAdminPayload) =>
    unwrap(apiClient.post<ApiSuccess<InstituteAdmin>>(`/platform/institutes/${uuid}/admins`, payload)),

  resendAdminInvite: (uuid: string, adminUuid: string) =>
    unwrap(
      apiClient.post<ApiSuccess<InstituteAdmin>>(
        `/platform/institutes/${uuid}/admins/${adminUuid}/resend-invite`,
        {},
      ),
    ),

  removeAdmin: (uuid: string, adminUuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/platform/institutes/${uuid}/admins/${adminUuid}`)),

  /** Re-sends the set-password link to the institute's first admin. */
  resendInvite: (uuid: string) =>
    unwrap(apiClient.post<ApiSuccess<null>>(`/platform/institutes/${uuid}/resend-invite`, {})),

  /**
   * Sets the institute administrator's password by hand, for support. The account
   * is flagged to force a change at their next sign-in.
   */
  setAdminPassword: (uuid: string, payload: { newPassword: string; confirmPassword: string }) =>
    unwrap(
      apiClient.post<ApiSuccess<{ email: string; name: string }>>(
        `/platform/institutes/${uuid}/admin-password`,
        payload,
      ),
    ),

  stats: () => unwrap(apiClient.get<ApiSuccess<PlatformStats>>('/platform/stats')),

  /** The address at a pin, looked up through our server (never from the browser). */
  reverseGeocode: (latitude: number, longitude: number) =>
    unwrap(
      apiClient.get<ApiSuccess<ReverseGeocodedAddress>>('/platform/geocode/reverse', {
        params: { lat: latitude, lng: longitude },
      }),
    ),

  /** Licence money received this month and last, and what is overdue. */
  subscriptionIncome: () =>
    unwrap(apiClient.get<ApiSuccess<SubscriptionIncome>>('/platform/subscription-income')),

  /** One page of what an institute has paid us, newest first. */
  subscriptionPayments: async (
    uuid: string,
    params: { page: number; limit: number },
  ): Promise<SubscriptionPaymentsResult> => {
    const response = await apiClient.get<
      ApiSuccess<{ items: SubscriptionPayment[]; summary: SubscriptionSummary }>
    >(`/platform/institutes/${uuid}/subscription-payments`, { params });
    const { items, summary } = response.data.data;

    return {
      items,
      summary,
      meta: response.data.meta ?? { page: 1, limit: params.limit, total: items.length, totalPages: 1 },
    };
  },

  recordSubscriptionPayment: (uuid: string, payload: SubscriptionPaymentPayload) =>
    unwrap(
      apiClient.post<ApiSuccess<SubscriptionPayment>>(
        `/platform/institutes/${uuid}/subscription-payments`,
        payload,
      ),
    ),

  /** One payment's receipt. Audited on the server. */
  subscriptionReceipt: (uuid: string, paymentUuid: string) =>
    unwrap(
      apiClient.get<ApiSuccess<SubscriptionReceipts>>(
        `/platform/institutes/${uuid}/subscription-payments/${paymentUuid}/receipt`,
      ),
    ),

  /** Every receipt for an institute, newest first. Audited on the server. */
  subscriptionReceipts: (uuid: string) =>
    unwrap(
      apiClient.get<ApiSuccess<SubscriptionReceipts>>(
        `/platform/institutes/${uuid}/subscription-payments/receipts`,
      ),
    ),
};
